<script setup lang="ts">
/**
 * CapacitorDisplay — Capacitor stats: capacity, recharge, stability
 */
import type { CapacitorStats } from '../../../shared/types';

const props = defineProps<{
  capacitor: CapacitorStats;
}>();

function fmt(n: number): string {
  if (n === 0) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(1);
}
</script>

<template>
  <div class="capacitor-display">
    <div class="cap-row">
      <span class="cap-label">Capacity</span>
      <span class="cap-value">{{ fmt(props.capacitor.capacity) }} GJ</span>
    </div>
    <div class="cap-row">
      <span class="cap-label">Recharge Time</span>
      <span class="cap-value">{{ props.capacitor.rechargeTime.toFixed(0) }}s</span>
    </div>
    <div class="cap-row">
      <span class="cap-label">Peak Recharge</span>
      <span class="cap-value">{{ fmt(props.capacitor.peakRecharge) }} GJ/s</span>
    </div>
    <div class="cap-row" v-if="props.capacitor.usage > 0">
      <span class="cap-label">Usage</span>
      <span class="cap-value">{{ fmt(props.capacitor.usage) }} GJ/s</span>
    </div>
    <div class="cap-row stability">
      <span class="cap-label">Stability</span>
      <span
        class="cap-stability"
        :class="props.capacitor.stable ? 'stable' : 'unstable'"
      >
        <template v-if="props.capacitor.stable">
          Stable at 100%
        </template>
        <template v-else>
          {{ props.capacitor.stablePercent.toFixed(0) }}%
        </template>
      </span>
    </div>
  </div>
</template>

<style scoped>
.capacitor-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cap-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.cap-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.cap-value {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.cap-stability {
  font-size: 0.88rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cap-stability.stable {
  color: var(--accent-green);
}

.cap-stability.unstable {
  color: var(--accent-orange);
}
</style>
